import React from "react";
import PropTypes from "prop-types";
import { Bell } from "lucide-react";

import { clx, formatDate } from "../../../utils/Helpers";
import "./NotificationItem.scss";

/**
 * Single notification row shared by the navbar dropdown and the full page.
 * `variant` drives body line-clamp and the unread background treatment:
 *   - "dropdown": 1-line body, unread rows get a subtle background.
 *   - "page":     2-line body, no row background (dot + icon carry the state).
 * Clicking the row marks it seen upstream, so the leading dot is a pure
 * unread indicator (no explicit mark-as-read control).
 */
const NotificationItem = ({ title, body, createdAt, isSeen, variant, onClick }) => {
  const interactive = typeof onClick === "function";

  return (
    <div
      className={clx(
        "notification-item",
        `notification-item--${variant}`,
        isSeen ? "notification-item--seen" : "notification-item--unseen"
      )}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
    >
      <span className="notification-item__indicator" aria-hidden="true">
        {!isSeen ? <span className="notification-item__dot" /> : null}
      </span>
      <span className="notification-item__icon" aria-hidden="true">
        <Bell size={18} strokeWidth={1.75} />
      </span>
      <div className="notification-item__content">
        <span className="notification-item__title">{title}</span>
        <span className="notification-item__body">{body}</span>
        <span className="notification-item__date">
          {formatDate(createdAt, "DD MMM YYYY")}
        </span>
      </div>
    </div>
  );
};

NotificationItem.propTypes = {
  title: PropTypes.string,
  body: PropTypes.string,
  createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  isSeen: PropTypes.bool,
  variant: PropTypes.oneOf(["dropdown", "page"]),
  onClick: PropTypes.func,
};

NotificationItem.defaultProps = {
  variant: "page",
};

export default NotificationItem;
